import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useAPI } from 'utils/api';
import { useTranslation } from 'react-i18next';
import { showToast } from 'components/atomic/Toast/Toast';
import { AB899ModuleType } from 'types/ProductDetailsType';
import { FDA_REGULATIONS_LINK } from 'i18n/globalConstants';
import { ReactComponent as ExpandIcon } from 'assets/icons/svg/expand.svg';
import {
  TestResultsWithUniversalLotType,
  ProductTestedType,
} from 'types/TestResultsTypes';
import SearchableDropDown from 'components/atomic/SearchableDropDown';
import dateDifferenceCounter from 'utils/dateDifferenceCounter';
import ModuleWrapper from 'components/wrappers/ModuleWrapper';
import HtmlWrapper from 'components/wrappers/HtmlWrapper';
import ToxicElementsTable from './ToxicElementsTable';
import Button from 'components/atomic/Button';
import Input from 'components/atomic/Input';
import envVars from 'env-vars';

type AB899DrawerProps = {
  brandId: string;
  drawerTitle: string;
  drawerData: AB899ModuleType;
};

type OptionType = {
  value: string;
  label: string;
};

const API_URL =
  envVars.REACT_APP_STAND_ALONE_MODE === 'false'
    ? envVars.REACT_APP_API_URL
    : '';

const AB899Drawer: React.FC<AB899DrawerProps> = ({
  brandId = '',
  drawerData,
  drawerTitle,
}) => {
  const [lotNumber, setLotnumber] = useState<string>('');
  const [upcNumber, setUpcNumber] = useState<OptionType | null>(null);
  const [expandedSection, setExpandedSection] = useState<number>(-1);
  const [resultsLoading, setResultsLoading] = useState<boolean>(false);
  const [testResults, setTestResults] = useState<
    TestResultsWithUniversalLotType[]
  >([]);
  const { t } = useTranslation('translation', {
    keyPrefix: 'drawers.ab899Drawer',
  });

  const {
    preLookup,
    postLookup,
    enableSearchByUpc,
    heavyMetalsResults,
    plasticizersResults,
    enableSearchByLotNumber,
    customizeDisclaimerText,
    customizeNoMessageResult,
    pesticidesAndGlyphosateResults,
    customizeSearchInstructionsMessage,
  } = drawerData;

  const endpoint = `ab899/${brandId}?${
    lotNumber
      ? `lotNumber=${
          drawerData.enableSearchByLotNumber.ignoreSpecialCharacters
            ? lotNumber.replace(/[^a-zA-Z0-9]/g, '')
            : lotNumber
        }&`
      : ''
  }${
    upcNumber
      ? `upcNumber=${
          drawerData.enableSearchByUpc.ignoreSpecialCharacters
            ? upcNumber.value.replace(/[^a-zA-Z0-9\s]/g, '')
            : upcNumber.value
        }`
      : ''
  }`;

  const [fetchLotDetails] = useAPI({
    endpoint,
    method: 'GET',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!lotNumber && !upcNumber) {
      showToast({
        message: t('errors.emptyLotOrUpcNumber'),
        type: 'error',
      });
    } else {
      setResultsLoading(true);
      try {
        let lotDetails = await fetchLotDetails();
        setTestResults(lotDetails?.testResults);
        if (lotDetails?.testResults?.length === 1) setExpandedSection(0);
      } catch (error: any) {
        showToast({
          message: customizeNoMessageResult?.enabled
            ? customizeNoMessageResult.message
            : t('errors.noRecordFound'),
          type: 'error',
        });
      }
      setResultsLoading(false);
    }
  };

  const fetchServerRecords = async (query: string) => {
    const upcQuery = enableSearchByUpc.ignoreSpecialCharacters
      ? query.replace(/[^a-zA-Z0-9\s]/g, '')
      : query || '';
    try {
      const endpoint = `${API_URL}/app_api/ab899/search/${brandId}?upcNumber=${upcQuery}&upcSearch=${
        enableSearchByUpc.searchByUPC === true ? 'true' : 'false'
      }&nameSearch=${
        enableSearchByUpc?.searchByProductName === true ? 'true' : 'false'
      }`;
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching server results:', error);
      return [];
    }
  };

  const getDefaultSearchInstructions = () => {
    if (enableSearchByUpc.enabled && enableSearchByLotNumber.enabled) {
      if (
        enableSearchByUpc.searchByUPC &&
        enableSearchByUpc.searchByProductName
      )
        return t('enterNameOrUpcOrLotNumber');
      else if (enableSearchByUpc.searchByUPC) return t('enterUpcOrLotNumber');
      else if (enableSearchByUpc.searchByProductName)
        return t('enterNameOrLotNumber');
    } else if (enableSearchByUpc.enabled && !enableSearchByLotNumber.enabled) {
      if (
        enableSearchByUpc.searchByUPC &&
        enableSearchByUpc.searchByProductName
      )
        return t('enterNameOrUpcNumber');
      else if (enableSearchByUpc.searchByUPC) return t('enterUpcNumber');
      else if (enableSearchByUpc.searchByProductName) return t('enterName');
    } else if (!enableSearchByUpc.enabled && enableSearchByLotNumber.enabled)
      return t('enterLotNumber');
    return t('enterNameOrUpcOrLotNumber');
  };

  const getUpcSearchPlaceholderText = () => {
    if (upcNumber) {
      if (
        enableSearchByUpc?.enabled &&
        enableSearchByUpc?.searchByUPC &&
        enableSearchByUpc?.searchByProductName
      )
        return t('upcAndProductInputPlaceholder');
      else if (
        enableSearchByUpc?.enabled &&
        enableSearchByUpc?.searchByUPC &&
        !enableSearchByUpc?.searchByProductName
      )
        return t('upcNumberInputPlaceholder');
      else if (
        enableSearchByUpc?.enabled &&
        !enableSearchByUpc?.searchByUPC &&
        enableSearchByUpc?.searchByProductName
      )
        return t('upcProductInputPlaceholder');
      else return t('upcNumberInputPlaceholder');
    } else {
      if (
        enableSearchByUpc?.enabled &&
        enableSearchByUpc?.searchByUPC &&
        enableSearchByUpc?.searchByProductName
      )
        return t('upcAndProductEmptyPlaceholder');
      else if (
        enableSearchByUpc?.enabled &&
        enableSearchByUpc?.searchByUPC &&
        !enableSearchByUpc?.searchByProductName
      )
        return t('upcNumberEmptyPlaceholder');
      else if (
        enableSearchByUpc?.enabled &&
        !enableSearchByUpc?.searchByUPC &&
        enableSearchByUpc?.searchByProductName
      )
        return t('upcProductEmptyPlaceholder');
      else return t('upcNumberEmptyPlaceholder');
    }
  };

  const legalDisclaimer = customizeDisclaimerText?.enabled ? (
    <div
      tabIndex={0}
      dangerouslySetInnerHTML={{ __html: customizeDisclaimerText?.text || '' }}
      className='w-full text-xxs px-6 pt-1.5 pb-3 text-justify [&_a]:underline [&_a]:text-brij'
    />
  ) : (
    <p tabIndex={0} className='text-xxs px-6 pt-1.5 pb-3 text-justify'>
      {t('disclaimer')}
      <a
        href={FDA_REGULATIONS_LINK}
        target='_blank'
        rel='noreferrer'
        className='text-brij underline'
      >
        {t('disclaimerLink')}
      </a>
      .
    </p>
  );

  return (
    <ModuleWrapper drawerTitle={drawerTitle}>
      <div className='relative w-full flex flex-col items-center justify-center gap-3'>
        <HtmlWrapper html={testResults?.length > 0 ? postLookup : preLookup} />
        {!testResults || testResults.length === 0 ? (
          <section className='relative w-full flex flex-col mt-2.5 pb-10 gap-3 rounded-xl border border-solid border-lightgray overflow-hidden'>
            <div className='relative py-1.5 w-full text-center bg-minimalGray'>
              <h3
                tabIndex={0}
                className='font-bold uppercase text-lg text-black tracking-[0.5px]'
              >
                {t('testResultsLookup')}
              </h3>
            </div>
            {(!testResults || testResults?.length === 0) && (
              <div className='relative flex flex-col px-3 gap-3'>
                {customizeSearchInstructionsMessage?.enabled ? (
                  <>
                    {customizeSearchInstructionsMessage?.text ? (
                      <p
                        tabIndex={0}
                        className='px-1.5 py-2 font-medium text-sm text-black tracking-[0.3px]'
                      >
                        {customizeSearchInstructionsMessage.text || ''}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p
                    tabIndex={0}
                    className='px-1.5 py-2 font-medium text-sm text-black tracking-[0.3px]'
                  >
                    {getDefaultSearchInstructions()}
                  </p>
                )}
                <form
                  onSubmit={handleSubmit}
                  className='relative flex flex-col gap-3'
                >
                  {enableSearchByUpc?.enabled && (
                    <SearchableDropDown
                      type='text'
                      hideChevron
                      autoComplete='off'
                      name='upc-number-search'
                      ignoreSpecialCharacters={
                        enableSearchByUpc.ignoreSpecialCharacters || false
                      }
                      placeholder={getUpcSearchPlaceholderText()}
                      required={enableSearchByUpc.required || false}
                      customNoResultsMessage={
                        upcNumber?.value && upcNumber.value.length > 2
                          ? t('noProductsFoundMessage')
                          : t('typeToSearchMessage')
                      }
                      selected={{
                        value: upcNumber?.value || '',
                        label: upcNumber?.label || '',
                      }}
                      fetchProductsTested={(query: string) =>
                        fetchServerRecords(query)
                      }
                      onChange={(option) =>
                        setUpcNumber({
                          value: option.value,
                          label: option.label,
                        })
                      }
                      options={[]}
                    />
                  )}
                  {enableSearchByLotNumber?.enabled && (
                    <Input
                      value={lotNumber}
                      name='lot-number-input'
                      autoComplete='lot-number-input'
                      required={enableSearchByLotNumber?.required || false}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setLotnumber(event.target.value)
                      }
                      placeholder={
                        lotNumber
                          ? t('lotNumberInputPlaceholder')
                          : `${t('lotNumberEmptyInputPlaceholder')} ${
                              enableSearchByUpc.enabled &&
                              !enableSearchByLotNumber.required
                                ? t('optionalPlaceholderText')
                                : ''
                            }`
                      }
                    />
                  )}
                  <Button
                    type='submit'
                    variant='dark'
                    title={t('submitLotNumberButton')}
                    disabled={!lotNumber && !upcNumber}
                    loading={resultsLoading}
                  />
                </form>
              </div>
            )}
            {legalDisclaimer}
          </section>
        ) : (
          <div className='relative w-full flex flex-col items-center justify-start gap-3'>
            {testResults.length > 0 &&
              testResults?.map(
                (result: TestResultsWithUniversalLotType, index: number) =>
                  result?.expirationDate &&
                  drawerData?.heavyMetalsResults.hidePostExpirationResults &&
                  drawerData.pesticidesAndGlyphosateResults
                    .hidePostExpirationResults &&
                  drawerData.plasticizersResults.hidePostExpirationResults &&
                  dateDifferenceCounter(result?.expirationDate) > 31 ? null : (
                    <section
                      key={result._id}
                      className='relative w-full flex flex-col items-center justify-start rounded-xl border border-solid border-lightgray overflow-hidden'
                    >
                      <button
                        type='button'
                        aria-expanded={expandedSection === index}
                        aria-controls={`expandable-content-${index}`}
                        aria-label={`${t('ariaToggleDetails')} ${result.lotNo}`}
                        className='relative w-full flex flex-row items-center justify-between px-4 py-2 duration-200 bg-minimalGray focus-visible:bg-gray-200'
                        onClick={() =>
                          expandedSection === index
                            ? setExpandedSection(-1)
                            : setExpandedSection(index)
                        }
                      >
                        <div className='relative w-full flex flex-col items-start'>
                          <p
                            tabIndex={0}
                            className='w-full font-medium text-xs text-muted text-left text-wrap text-ellipsis whitespace-nowrap overflow-hidden'
                          >
                            {result.productsTested
                              ?.map(
                                (product: ProductTestedType) =>
                                  `${product.name}${
                                    product.upc ? ` (${product.upc})` : ''
                                  }`
                              )
                              .join(', ')}
                          </p>
                          <h3 tabIndex={0} className='font-bold text-left'>
                            {t('lotNumberHeading')}: {result.lotNo}
                          </h3>
                        </div>
                        <ExpandIcon
                          className={`relative min-w-[18px] min-h-[18px] transform duration-150 ${
                            index === expandedSection
                              ? 'rotate-180'
                              : 'rotate-0'
                          }`}
                          fill='#000000'
                          height={18}
                          width={18}
                        />
                      </button>
                      {expandedSection === index && (
                        <div
                          aria-controls={`expandable-content-${index}`}
                          className='relative w-full flex flex-col items-center justify-start p-3 gap-3'
                        >
                          {result?.heavyMetals &&
                          heavyMetalsResults?.enabled &&
                          result?.heavyMetals?.length > 0 ? (
                            <ToxicElementsTable
                              startExpanded
                              key='heavy-metals-table'
                              title={t('heavyMetalsHeading')}
                              toxicElements={result.heavyMetals}
                              toxicElementsResults={heavyMetalsResults}
                              expirationDate={result.expirationDate || null}
                              testingDate={result.testingDate || null}
                              testResultsFile={
                                result?.testResultsFile &&
                                drawerData?.heavyMetalsResults
                                  .includeLinkToTestResultsDocument?.enabled
                                  ? {
                                      fileLink: result?.testResultsFile || '',
                                      linkText:
                                        result?.testResultsFile &&
                                        drawerData?.heavyMetalsResults
                                          .includeLinkToTestResultsDocument
                                          ?.enabled
                                          ? heavyMetalsResults
                                              .includeLinkToTestResultsDocument
                                              .linkText
                                          : t('viewFullReportButton'),
                                    }
                                  : null
                              }
                            />
                          ) : result?.universalTestResults &&
                            heavyMetalsResults?.enabled &&
                            result?.universalTestResults?.heavyMetals &&
                            result?.universalTestResults?.heavyMetals?.length >
                              0 ? (
                            <ToxicElementsTable
                              startExpanded
                              key='heavy-metals-table'
                              title={t('heavyMetalsHeading')}
                              toxicElements={
                                result?.universalTestResults?.heavyMetals
                              }
                              toxicElementsResults={heavyMetalsResults}
                              expirationDate={
                                result?.universalTestResults?.expirationDate ||
                                null
                              }
                              testingDate={
                                result?.universalTestResults?.testingDate ||
                                null
                              }
                              testResultsFile={
                                result?.universalTestResults?.testResultsFile &&
                                drawerData?.heavyMetalsResults
                                  ?.includeLinkToTestResultsDocument?.enabled
                                  ? {
                                      fileLink:
                                        result?.universalTestResults
                                          ?.testResultsFile || '',
                                      linkText:
                                        result?.universalTestResults
                                          ?.testResultsFile &&
                                        drawerData?.heavyMetalsResults
                                          ?.includeLinkToTestResultsDocument
                                          ?.enabled
                                          ? heavyMetalsResults
                                              ?.includeLinkToTestResultsDocument
                                              ?.linkText
                                          : t('viewFullReportButton'),
                                    }
                                  : null
                              }
                            />
                          ) : null}
                          {result?.pesticidesAndGlyphosateElements &&
                          pesticidesAndGlyphosateResults?.enabled &&
                          result?.pesticidesAndGlyphosateElements?.length >
                            0 ? (
                            <ToxicElementsTable
                              key='pesticides-and-glyphosate-table'
                              title={t('pesticidesAndGlyphosateHeading')}
                              toxicElements={
                                result.pesticidesAndGlyphosateElements
                              }
                              toxicElementsResults={
                                drawerData.pesticidesAndGlyphosateResults
                              }
                              expirationDate={result.expirationDate || null}
                              testingDate={result.testingDate || null}
                              testResultsFile={
                                result?.testResultsFile &&
                                drawerData?.pesticidesAndGlyphosateResults
                                  .includeLinkToTestResultsDocument?.enabled
                                  ? {
                                      fileLink: result?.testResultsFile || '',
                                      linkText:
                                        result?.testResultsFile &&
                                        drawerData
                                          ?.pesticidesAndGlyphosateResults
                                          .includeLinkToTestResultsDocument
                                          ?.enabled
                                          ? pesticidesAndGlyphosateResults
                                              .includeLinkToTestResultsDocument
                                              .linkText
                                          : t('viewFullReportButton'),
                                    }
                                  : null
                              }
                            />
                          ) : result?.universalTestResults &&
                            pesticidesAndGlyphosateResults?.enabled &&
                            result?.universalTestResults
                              ?.pesticidesAndGlyphosateElements &&
                            result?.universalTestResults
                              ?.pesticidesAndGlyphosateElements?.length > 0 ? (
                            <ToxicElementsTable
                              key='pesticides-and-glyphosate-table'
                              title={t('pesticidesAndGlyphosateHeading')}
                              toxicElements={
                                result?.universalTestResults
                                  ?.pesticidesAndGlyphosateElements
                              }
                              toxicElementsResults={
                                drawerData?.pesticidesAndGlyphosateResults
                              }
                              expirationDate={
                                result?.universalTestResults?.expirationDate ||
                                null
                              }
                              testingDate={
                                result?.universalTestResults?.testingDate ||
                                null
                              }
                              testResultsFile={
                                result?.universalTestResults?.testResultsFile &&
                                drawerData?.pesticidesAndGlyphosateResults
                                  ?.includeLinkToTestResultsDocument?.enabled
                                  ? {
                                      fileLink:
                                        result?.universalTestResults
                                          ?.testResultsFile || '',
                                      linkText:
                                        result?.universalTestResults
                                          ?.testResultsFile &&
                                        drawerData
                                          ?.pesticidesAndGlyphosateResults
                                          ?.includeLinkToTestResultsDocument
                                          ?.enabled
                                          ? pesticidesAndGlyphosateResults
                                              ?.includeLinkToTestResultsDocument
                                              ?.linkText
                                          : t('viewFullReportButton'),
                                    }
                                  : null
                              }
                            />
                          ) : null}
                          {result.plasticizersElements &&
                          plasticizersResults?.enabled &&
                          result.plasticizersElements?.length > 0 ? (
                            <ToxicElementsTable
                              key='plasticizers-table'
                              title={t('plasticizersHeading')}
                              toxicElements={result.plasticizersElements}
                              toxicElementsResults={
                                drawerData.plasticizersResults
                              }
                              expirationDate={result.expirationDate || null}
                              testingDate={result.testingDate || null}
                              testResultsFile={
                                result?.testResultsFile &&
                                drawerData?.plasticizersResults
                                  .includeLinkToTestResultsDocument?.enabled
                                  ? {
                                      fileLink: result?.testResultsFile || '',
                                      linkText:
                                        result?.testResultsFile &&
                                        drawerData?.plasticizersResults
                                          .includeLinkToTestResultsDocument
                                          ?.enabled
                                          ? plasticizersResults
                                              .includeLinkToTestResultsDocument
                                              .linkText
                                          : t('viewFullReportButton'),
                                    }
                                  : null
                              }
                            />
                          ) : result?.universalTestResults &&
                            plasticizersResults?.enabled &&
                            result?.universalTestResults
                              ?.plasticizersElements &&
                            result?.universalTestResults?.plasticizersElements
                              ?.length > 0 ? (
                            <ToxicElementsTable
                              key='plasticizers-table'
                              title={t('plasticizersHeading')}
                              toxicElements={
                                result?.universalTestResults
                                  ?.plasticizersElements
                              }
                              toxicElementsResults={
                                drawerData?.plasticizersResults
                              }
                              expirationDate={
                                result?.universalTestResults?.expirationDate ||
                                null
                              }
                              testingDate={
                                result?.universalTestResults?.testingDate ||
                                null
                              }
                              testResultsFile={
                                result?.universalTestResults?.testResultsFile &&
                                drawerData?.plasticizersResults
                                  ?.includeLinkToTestResultsDocument?.enabled
                                  ? {
                                      fileLink:
                                        result?.universalTestResults
                                          ?.testResultsFile || '',
                                      linkText:
                                        result?.universalTestResults
                                          ?.testResultsFile &&
                                        drawerData?.plasticizersResults
                                          ?.includeLinkToTestResultsDocument
                                          ?.enabled
                                          ? plasticizersResults
                                              ?.includeLinkToTestResultsDocument
                                              ?.linkText
                                          : t('viewFullReportButton'),
                                    }
                                  : null
                              }
                            />
                          ) : null}
                        </div>
                      )}
                    </section>
                  )
              )}
            {testResults?.length > 0 && (
              <div className='relative w-full'>
                <Button
                  type='button'
                  variant='dark'
                  title={t('startOverButton')}
                  onClick={() => {
                    setLotnumber('');
                    setUpcNumber(null);
                    setTestResults([]);
                    setExpandedSection(-1);
                  }}
                />
              </div>
            )}
            {legalDisclaimer}
          </div>
        )}
      </div>
    </ModuleWrapper>
  );
};

export default AB899Drawer;
